import { useState, useEffect, useRef } from 'react';
import { Calendar, Clock, X } from 'lucide-react';

const StepAvailability = ({ formData, handleInputChange }) => {
  const [blockedDates, setBlockedDates] = useState(formData.blockedDates || []);
  const isInternalUpdate = useRef(false);
  const prevFormDataBlockedDates = useRef(formData.blockedDates);
  const lastSetValue = useRef(null); // Track the last value we set via handleInputChange

  // Sync from parent to local state only when parent changes externally
  useEffect(() => {
    const formDataBlockedDates = formData.blockedDates || [];
    const formDataStr = JSON.stringify([...formDataBlockedDates].sort());
    const lastSetStr = lastSetValue.current ? JSON.stringify([...lastSetValue.current].sort()) : null;

    console.log('[StepAvailability] Sync effect triggered', {
      isInternalUpdate: isInternalUpdate.current,
      formDataBlockedDatesLength: formDataBlockedDates.length,
      currentBlockedDatesLength: blockedDates.length,
      lastSetValueLength: lastSetValue.current?.length || 0,
      formDataMatchesLastSet: formDataStr === lastSetStr
    });

    // Skip if this update came from our own internal changes
    if (isInternalUpdate.current) {
      console.log('[StepAvailability] Skipping sync - internal update');
      isInternalUpdate.current = false;
      // Update lastSetValue to match what we just set
      lastSetValue.current = formDataBlockedDates;
      prevFormDataBlockedDates.current = formDataBlockedDates;
      return;
    }
    
    // If formData matches what we last set, don't update (avoid unnecessary updates)
    if (formDataStr === lastSetStr && lastSetStr !== null) {
      console.log('[StepAvailability] Skipping - formData matches last set value');
      return;
    }
    
    // Only update if parent data actually changed from what we expect
    const prevStr = JSON.stringify([...(prevFormDataBlockedDates.current || [])].sort());
    const currentStr = formDataStr;
    
    console.log('[StepAvailability] Comparing dates', {
      prevStr,
      currentStr,
      areEqual: prevStr === currentStr,
      prevLength: (prevFormDataBlockedDates.current || []).length,
      currentLength: formDataBlockedDates.length
    });
    
    if (prevStr !== currentStr) {
      console.log('[StepAvailability] Updating local state from parent - parent changed externally');
      setBlockedDates(formDataBlockedDates);
      prevFormDataBlockedDates.current = formDataBlockedDates;
      lastSetValue.current = formDataBlockedDates; // Update lastSetValue
    } else {
      console.log('[StepAvailability] No update needed - values match');
    }
  }, [formData.blockedDates]);

  // Generate date range from startDate to endDate
  const getDateRange = () => {
    if (!formData.startDate || !formData.endDate) return [];
    const start = new Date(formData.startDate);
    const end = new Date(formData.endDate);
    const dates = [];
    const current = new Date(start);
    while (current <= end) {
      dates.push(new Date(current).toISOString().split('T')[0]);
      current.setDate(current.getDate() + 1);
    }
    return dates;
  };

  const availableDates = getDateRange();

  const handleBlockedDateClick = (date) => {
    console.log('[StepAvailability] Date clicked', { date, currentBlockedDates: blockedDates });
    const dateObj = new Date(date);
    const isPast = dateObj < new Date().setHours(0, 0, 0, 0);
    if (isPast) {
      console.log('[StepAvailability] Date is in the past, ignoring');
      return;
    }

    // Toggle individual date
    let updated;
    if (isDateBlocked(date)) {
      // Remove date
      updated = blockedDates.filter(d => d !== date);
      console.log('[StepAvailability] Removing date', { date, updated });
    } else {
      // Add date
      updated = [...new Set([...blockedDates, date])].sort();
      console.log('[StepAvailability] Adding date', { date, updated });
    }

    // Set flag and update refs BEFORE updating state
    isInternalUpdate.current = true;
    prevFormDataBlockedDates.current = updated;
    lastSetValue.current = updated; // Track what we're setting
    console.log('[StepAvailability] Set isInternalUpdate to true and updated refs', { updated });
    
    // Update local state FIRST, then parent (so UI updates immediately)
    setBlockedDates(updated);
    handleInputChange('blockedDates', updated);
  };

  const handleMonthToggle = (monthYear) => {
    console.log('[StepAvailability] Month toggle clicked', { monthYear });
    // Get all dates in this month
    const [year, month] = monthYear.split('-').map(Number);
    const monthDates = availableDates.filter(date => {
      const dateObj = new Date(date);
      return dateObj.getFullYear() === year && dateObj.getMonth() === month - 1;
    });

    if (monthDates.length === 0) {
      console.log('[StepAvailability] No dates in month');
      return;
    }

    // Check if all dates in month are blocked
    const allBlocked = monthDates.every(date => isDateBlocked(date));
    console.log('[StepAvailability] Month blocked status', { allBlocked, monthDates });

    let updated;
    if (allBlocked) {
      // Unblock all dates in month
      updated = blockedDates.filter(d => !monthDates.includes(d));
      console.log('[StepAvailability] Unblocking month', { updated });
    } else {
      // Block all dates in month
      updated = [...new Set([...blockedDates, ...monthDates])].sort();
      console.log('[StepAvailability] Blocking month', { updated });
    }

    // Set flag and update refs BEFORE updating state
    isInternalUpdate.current = true;
    prevFormDataBlockedDates.current = updated;
    lastSetValue.current = updated; // Track what we're setting
    console.log('[StepAvailability] Set isInternalUpdate to true and updated refs', { updated });

    // Update local state FIRST, then parent (so UI updates immediately)
    setBlockedDates(updated);
    handleInputChange('blockedDates', updated);
  };

  const clearAllBlockedDates = () => {
    console.log('[StepAvailability] Clear all clicked');
    const updated = [];
    // Set flag and update prev ref BEFORE updating state
    isInternalUpdate.current = true;
    prevFormDataBlockedDates.current = updated;
    console.log('[StepAvailability] Set isInternalUpdate to true and updated prevFormDataBlockedDates', { updated });
    // Update parent FIRST, then local state
    handleInputChange('blockedDates', updated);
    setBlockedDates(updated);
  };


  const removeBlockedDate = (dateToRemove) => {
    console.log('[StepAvailability] Remove date clicked', { dateToRemove });
    const updated = blockedDates.filter(d => d !== dateToRemove);
    // Set flag and update prev ref BEFORE updating state
    isInternalUpdate.current = true;
    prevFormDataBlockedDates.current = updated;
    console.log('[StepAvailability] Set isInternalUpdate to true and updated prevFormDataBlockedDates', { updated });
    // Update parent FIRST, then local state
    handleInputChange('blockedDates', updated);
    setBlockedDates(updated);
  };

  const removeBlockedRange = (range) => {
    console.log('[StepAvailability] Remove range clicked', { range });
    const start = new Date(range.start);
    const end = new Date(range.end);
    const dates = [];
    const current = new Date(start);
    while (current <= end) {
      dates.push(new Date(current).toISOString().split('T')[0]);
      current.setDate(current.getDate() + 1);
    }
    const updated = blockedDates.filter(d => !dates.includes(d));
    // Set flag and update prev ref BEFORE updating state
    isInternalUpdate.current = true;
    prevFormDataBlockedDates.current = updated;
    console.log('[StepAvailability] Set isInternalUpdate to true and updated prevFormDataBlockedDates', { updated });
    // Update parent FIRST, then local state
    handleInputChange('blockedDates', updated);
    setBlockedDates(updated);
  };

  // Group blocked dates into ranges for display
  const getBlockedRanges = () => {
    if (blockedDates.length === 0) return [];
    // Ensure unique dates before processing
    const uniqueDates = [...new Set(blockedDates)].sort();
    const ranges = [];
    let currentRange = { start: uniqueDates[0], end: uniqueDates[0] };

    for (let i = 1; i < uniqueDates.length; i++) {
      const prev = new Date(uniqueDates[i - 1]);
      const curr = new Date(uniqueDates[i]);
      const diffDays = (curr - prev) / (1000 * 60 * 60 * 24);

      if (diffDays === 1) {
        currentRange.end = uniqueDates[i];
      } else {
        ranges.push(currentRange);
        currentRange = { start: uniqueDates[i], end: uniqueDates[i] };
      }
    }
    ranges.push(currentRange);
    return ranges;
  };

  const blockedRanges = getBlockedRanges();

  const isDateBlocked = (date) => {
    return blockedDates.includes(date);
  };

  // Group dates by month
  const groupDatesByMonth = () => {
    const grouped = {};
    availableDates.forEach(date => {
      const dateObj = new Date(date);
      const year = dateObj.getFullYear();
      const month = dateObj.getMonth() + 1; // 1-12
      const key = `${year}-${month}`;
      
      if (!grouped[key]) {
        grouped[key] = {
          year,
          month,
          dates: [],
          monthName: dateObj.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
        };
      }
      grouped[key].dates.push(date);
    });
    
    return Object.values(grouped).sort((a, b) => {
      if (a.year !== b.year) return a.year - b.year;
      return a.month - b.month;
    });
  };

  const monthsData = groupDatesByMonth();

  const isMonthFullyBlocked = (monthDates) => {
    return monthDates.every(date => isDateBlocked(date));
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
          <Calendar className="w-4 h-4 text-emerald-600" />
          Booking Window (days in advance) *
        </label>
        <input
          type="number"
          min="1"
          required
          className="w-full px-4 py-3 border border-gray-200 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-200 bg-white/50"
          placeholder="30"
          value={formData.bookingWindow || 30}
          onChange={(e) => handleInputChange('bookingWindow', e.target.value)}
        />
        <p className="mt-1 text-xs text-gray-500">Minimum days in advance guests can book</p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
          <Clock className="w-4 h-4 text-emerald-600" />
          Minimum Stay (nights) *
        </label>
        <input
          type="number"
          min="1"
          required
          className="w-full px-4 py-3 border border-gray-200 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-200 bg-white/50"
          placeholder="2"
          value={formData.minStay || 1}
          onChange={(e) => handleInputChange('minStay', e.target.value)}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Maximum Stay (nights)
        </label>
        <input
          type="number"
          min="1"
          className="w-full px-4 py-3 border border-gray-200 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-200 bg-white/50"
          placeholder="14"
          value={formData.maxStay || ''}
          onChange={(e) => handleInputChange('maxStay', e.target.value)}
        />
        <p className="mt-1 text-xs text-gray-500">Leave empty for no maximum</p>
      </div>

      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <label className="flex items-center gap-3 cursor-pointer">
          <input
            type="checkbox"
            className="w-4 h-4 text-emerald-600 border-gray-300 rounded focus:ring-emerald-500"
            checked={formData.instantBooking || false}
            onChange={(e) => handleInputChange('instantBooking', e.target.checked)}
          />
          <span className="text-sm font-medium text-gray-700">Enable Instant Booking</span>
        </label>
        <p className="text-xs text-gray-500 mt-1 ml-7">
          Allow guests to book immediately without host approval
        </p>
      </div>

      {/* Blocked Dates Section */}
      {formData.startDate && formData.endDate && availableDates.length > 0 && (
        <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
          <div className="flex items-center justify-between mb-4">
            <label className="block text-sm font-medium text-gray-700 flex items-center gap-2">
              <X className="w-4 h-4 text-red-600" />
              Block Unavailable Dates
            </label>
            <div className="flex items-center gap-3">
              <p className="text-xs text-gray-500 hidden sm:block">Click dates to block them</p>
              {blockedDates.length > 0 && (
                <button
                  type="button"
                  onClick={clearAllBlockedDates}
                  className="text-xs text-red-600 hover:text-red-800 font-medium px-3 py-1.5 border border-red-300 rounded-lg hover:bg-red-50 transition-colors"
                >
                  Clear All
                </button>
              )}
            </div>
          </div>

          {/* Calendar by Month */}
          <div className="space-y-6">
            {monthsData.map((monthData) => {
              const monthKey = `${monthData.year}-${monthData.month}`;
              const firstDate = new Date(monthData.dates[0]);
              const firstDayOfWeek = firstDate.getDay();
              const isMonthBlocked = isMonthFullyBlocked(monthData.dates);

              return (
                <div key={monthKey} className="bg-white rounded-lg p-4 border border-gray-200">
                  {/* Month Header with Toggle */}
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-base font-semibold text-gray-900">{monthData.monthName}</h3>
                    <button
                      type="button"
                      onClick={() => handleMonthToggle(monthKey)}
                      className={`text-xs font-medium px-3 py-1.5 rounded-lg transition-colors ${
                        isMonthBlocked
                          ? 'bg-red-100 text-red-700 hover:bg-red-200 border border-red-300'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-300'
                      }`}
                    >
                      {isMonthBlocked ? 'Unblock Month' : 'Block Month'}
                    </button>
                  </div>

                  {/* Day Headers */}
                  <div className="grid grid-cols-7 gap-2 mb-2">
                    {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
                      <div key={day} className="text-center text-xs font-medium text-gray-500 py-1">
                        {day}
                      </div>
                    ))}
                  </div>

                  {/* Date Grid */}
                  <div className="grid grid-cols-7 gap-2">
                    {(() => {
                      const cells = [];
                      
                      // Add empty cells before first date
                      for (let i = 0; i < firstDayOfWeek; i++) {
                        cells.push(<div key={`empty-${monthKey}-${i}`} className="p-2" />);
                      }
                      
                      // Add date cells for this month
                      monthData.dates.forEach((date, index) => {
                        const dateObj = new Date(date);
                        const isBlocked = isDateBlocked(date);
                        const isPast = dateObj < new Date().setHours(0, 0, 0, 0);

                        cells.push(
                          <button
                            key={`date-${date}-${index}`}
                            type="button"
                            onClick={() => handleBlockedDateClick(date)}
                            disabled={isPast}
                            className={`
                              p-2 rounded-lg text-sm font-medium transition-all
                              ${isPast
                                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                : isBlocked
                                ? 'bg-red-200 text-red-900 hover:bg-red-300 border-2 border-red-400'
                                : 'bg-white text-gray-700 hover:bg-red-50 hover:text-red-700 border border-gray-200'
                              }
                            `}
                          >
                            {dateObj.getDate()}
                          </button>
                        );
                      });
                      
                      return cells;
                    })()}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Blocked Dates List */}
          {blockedRanges.length > 0 && (
            <div className="mt-4 p-3 bg-white rounded-lg border border-gray-200">
              <p className="text-xs font-medium text-gray-700 mb-2">Blocked Date Ranges:</p>
              <div className="space-y-2">
                {blockedRanges.map((range, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between px-3 py-2 bg-red-50 border border-red-200 rounded text-sm"
                  >
                    <span className="text-red-800 font-medium">
                      {formatDate(range.start)}
                      {range.end !== range.start && ` - ${formatDate(range.end)}`}
                    </span>
                    <button
                      type="button"
                      onClick={() => removeBlockedRange(range)}
                      className="text-red-600 hover:text-red-800"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {(!formData.startDate || !formData.endDate) && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <p className="text-sm text-yellow-800">
            Please set start and end dates in the Basic Info step to enable date blocking.
          </p>
        </div>
      )}
    </div>
  );
};

export default StepAvailability;

